// src/lib/pipelineVote.js
//
// Ticket 26 part B. The small rules behind the thumbs on a pipeline card
// (components/homepage/PipelineVote.jsx), kept here so a script can check them
// without rendering anything.
//
// WHO MAY VOTE is decided in the database (pipeline_vote_state and
// cast_pipeline_vote, migration 20261012100000), never here. The client only
// draws what it is told and turns the database's reason into words.

export const VOTE_UP = 1;
export const VOTE_DOWN = -1;
export const VOTE_NONE = 0;

// Pressing the thumb you already pressed takes the vote back; pressing the
// other one changes it. Returns the vote to send to cast_pipeline_vote.
export function nextVote(current, pressed) {
  return Number(current) === pressed ? VOTE_NONE : pressed;
}

// What a tap on a thumb does, given what pipeline_vote_state said about this
// reader and whether the page can open its sign-up pop-up.
//   'vote'     the reader may vote: cast it.
//   'sign_in'  the reader is signed out: open the sign-up pop-up, the same as
//              Save and Already Read do. Nothing is cast.
//   'none'     greyed thumb (signed in but hasn't read both books, own book,
//              book not in the catalogue), or state not read yet: do nothing.
export function tapAction(state, canPromptSignIn) {
  if (!state) return 'none';
  if (state.canVote === true) return 'vote';
  if (state.reason === 'not_signed_in' && canPromptSignIn === true) return 'sign_in';
  return 'none';
}

// The reasons the database gives (pipeline_vote_block). A greyed thumb always
// says why, so a missing hint is a bug: an unknown reason still gets a line.
const HINTS = {
  not_signed_in: 'Sign in and read both books to vote.',
  not_read_both: 'Read both books to vote.',
  own_book: 'You can’t vote on a pairing for your own book.',
  unavailable: 'Voting opens when both books are in our catalogue.',
};
export function voteHint(reason) {
  return HINTS[reason] || 'Voting isn’t open on this pairing.';
}

// A refusal from cast_pipeline_vote is a check violation whose hint reads
// 'pipeline_vote:<reason>'. Anything else is an ordinary failure (null).
export function reasonFromError(error) {
  const hint = typeof error?.hint === 'string' ? error.hint : '';
  return hint.startsWith('pipeline_vote:') ? hint.slice('pipeline_vote:'.length) : null;
}

// What the admin Pipelines screen prints for one row of admin_pipeline_votes:
// the vote counts, and, for a pipeline that can show, its place among the
// pipelines that can show under the same anchor ("N of M"). A pipeline that
// can't show (hidden, pending, flagged "won't show") has no place and takes no
// slot, so `place` is null for it. `overCap` is true only when the pipeline is
// ranked below the cap, so readers don't see it.
export function adminVoteSummary(row) {
  if (!row) return null;
  const up = Number(row.up_votes) || 0;
  const down = Number(row.down_votes) || 0;
  const rank = row.anchor_rank == null ? null : Number(row.anchor_rank);
  const total = row.anchor_total == null ? null : Number(row.anchor_total);
  const cap = row.cap == null ? null : Number(row.cap);
  return {
    counts: `${up} up, ${down} down`,
    place: rank == null || total == null ? null : `${rank} of ${total} under this anchor`,
    overCap: row.within_cap === false,
    cap,
  };
}
